/**
 * Electrode validation decisions (`app/services/electrode_validation_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';

// ========== Electrode validation (#1000) ==========
// Gated server-side by the `validation_radiologist` flag. The electrode name
// travels in the body because names may contain an apostrophe (RA').

/** The current user's per-electrode validations of an MRI record. */
export async function listElectrodeValidations(mriId: number): Promise<{ validations: ElectrodeValidation[] }> {
    const response = await client.request('GET', `/api/mri/${mriId}/electrode-validations`);
    return response as { validations: ElectrodeValidation[] };
}

/** Save (partial upsert) the current user's validation of one electrode. */
export async function saveElectrodeValidation(mriId: number, payload: ElectrodeValidationInput): Promise<ElectrodeValidation> {
    const response = await client.request('PUT', `/api/mri/${mriId}/electrode-validations`, payload);
    return response as ElectrodeValidation;
}

/** URL of a patient's validations CSV export. */
export function electrodeValidationsCsvUrl(patientId: number): string {
    return `/api/patient/${patientId}/electrode-validations.csv`;
}

/**
 * Payload accepted by ``createAnnotation`` / ``updateAnnotation``. All fields
 * are optional in the update path; ``timeStart``, ``text`` and ``color`` are
 * required on create.
 */
/** A per-electrode validation decision (#1000); null = not reviewed. */
export type ElectrodeDecision = 'yes' | 'no' | null;

export interface ElectrodeValidation {
    electrode: string;
    decision: ElectrodeDecision;
    comment: string;
    /** ISO 8601 UTC; empty when nothing has been stored yet. */
    updated_on: string;
}

/** Body of a save: `electrode` plus only the fields that changed. */
export interface ElectrodeValidationInput {
    electrode: string;
    decision?: ElectrodeDecision;
    comment?: string;
}
